import { _callBackend } from '@/_api/api';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { UsersV2FilterObject } from '@/_types/clients';
import { Company } from '@/_types/models/company';
import type { User } from '@/_types/models/user';

export const getAllUsers = async ({
  assigned_advisor_id,
  count,
  descending,
  hide_actives,
  hide_inactives,
  firm_group_id,
  only_ids,
  page,
  rows_per_page,
  search_term,
  selected_client_tags_filter: selected_client_tags_filter_from_attrs,
  sort_by,
  show_only_deleted,
  token,
}: APIRequest<UsersV2FilterObject>): APIResponsePromise<{
  count?: number;
  ids: number[];
  users: Array<Partial<User>>;
}> => {
  const adjustedPage: number = (page ?? 0) + 1;

  const selected_client_tags_filter: string =
    selected_client_tags_filter_from_attrs?.length > 0
      ? '&' +
        selected_client_tags_filter_from_attrs
          ?.map(
            (tag) => `selected_client_tags_filter[]=${encodeURIComponent(tag)}`,
          )
          .join('&')
      : '';

  // prettier-ignore
  return await _callBackend(
    `/v2/users?page=${adjustedPage
    }&rows_per_page=${rows_per_page
    }&sort_by=${sort_by
    }&descending=${descending
    }&search_term=${encodeURIComponent(search_term)
    }&hide_inactives=${hide_inactives
    }&hide_actives=${hide_actives
    }&assigned_advisor_id=${assigned_advisor_id
    }&firm_group_id=${firm_group_id
    }${count ? '&count=true' : ''
    }${only_ids ? '&only_ids=true' : ''
    }${show_only_deleted ? '&show_only_deleted=true' : ''
    }${selected_client_tags_filter}`,
    'GET', null, token);
};

export const findUsers = async ({
  token,
  search = '',
}: APIRequest<{
  search: string;
}>): APIResponsePromise<Array<User>> =>
  await _callBackend(
    `/v2/users/search?name=${encodeURIComponent(search)}`,
    'GET',
    null,
    token,
  );

export const restoreDeletedUser = async ({
  userId,
  token,
}: APIRequest<{ userId: number }>): APIResponsePromise =>
  await _callBackend(
    `/v2/users/${userId}/restore_deleted_user`,
    'POST',
    null,
    token,
  );

export const getAdminUsersByIds = async ({
  token,
  ids,
}: {
  token: string;
  ids: number[];
}): APIResponsePromise<Array<{ id: number; full_name: string }>> => {
  return await _callBackend(
    `/v2/users/by_ids?${ids.map((id) => `ids[]=${id}`).join('&')}`,
    'GET',
    null,
    token,
  );
};

export const getUserCompanies = async ({
  token,
  userId,
}: {
  token: string;
  userId: number;
}): APIResponsePromise<Array<Company>> => {
  return await _callBackend(
    `/v2/users/${userId}/user_assigned_companies`,
    'GET',
    null,
    token,
  );
};
